// The top bar's collaboration corner: the people in this project (click one to follow them), the inbox
// bell with its unread count, and the quiet "Following Ana · Stop" bar.
import type { PresencePeer } from "@filmsquare/realtime/presence";
import { useEffect, useRef } from "react";
import { useCollab } from "../../collab/CollabProvider.tsx";
import { firstName } from "../../collab/colors.ts";
import { useProjectPeople } from "../../collab/hooks.ts";
import { Avatar, CIcon } from "./Avatars.tsx";
import { InboxPanel } from "./Inbox.tsx";

function placeOf(p: PresencePeer): string {
  const w = p.presence?.where;
  return w?.label ?? (w ? w.kind : "in this production");
}

export function TopBarCollab() {
  const c = useCollab();
  const people = useProjectPeople();
  const wrap = useRef<HTMLDivElement>(null);
  const open = !!c?.inboxOpen;
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && c?.setInboxOpen(false);
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open, c]);
  if (!c) return null;
  const MAX = 4;
  // During an outage the count would be stale: show no badge until the server answers.
  const unread = c.offline ? 0 : c.unread;
  const shown = people.slice(0, MAX);
  const leader = people.find((p) => p.id === c.following) ?? null;
  return (
    <>
      {c.projectId && people.length > 0 && (
        // biome-ignore lint/a11y/useSemanticElements: a toolbar of people, not a form group
        <div className="pc-people" role="group" aria-label="People in this production">
          {shown.map((p) => {
            const isLeader = p.id === c.following;
            return (
              <button
                key={p.userId}
                type="button"
                className={`pc-person${isLeader ? " following" : ""}`}
                aria-pressed={isLeader}
                aria-label={isLeader ? `Stop following ${p.name}` : `Follow ${p.name}, ${placeOf(p)}`}
                title={`${p.name} · ${placeOf(p)}${isLeader ? " · following" : " · click to follow"}`}
                onClick={() => c.follow(isLeader ? null : p.id)}
              >
                <Avatar userId={p.userId} name={p.name} size={24} />
              </button>
            );
          })}
          {people.length > MAX && (
            <span
              className="pc-more"
              title={people
                .slice(MAX)
                .map((p) => p.name)
                .join(", ")}
            >
              +{people.length - MAX}
            </span>
          )}
        </div>
      )}
      <div className="menu-wrap" ref={wrap}>
        <button
          type="button"
          className="top-btn pc-bell"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={unread ? `Inbox, ${unread} unread` : "Inbox"}
          title="Inbox"
          onClick={() => c.setInboxOpen(!open)}
        >
          <CIcon name="bell" />
          {unread > 0 && <span className="pc-count">{unread > 99 ? "99+" : unread}</span>}
        </button>
        {open && <InboxPanel onClose={() => c.setInboxOpen(false)} />}
      </div>
      {c.following && (
        <div className="pc-follow-bar" role="status">
          {leader && <Avatar userId={leader.userId} name={leader.name} size={18} />}
          <span>
            Following {leader ? firstName(leader.name) : "…"}
            {leader?.presence?.where?.label ? ` · ${leader.presence.where.label}` : ""}
          </span>
          <button type="button" className="pc-follow-stop" onClick={() => c.follow(null)}>
            Stop <kbd>esc</kbd>
          </button>
        </div>
      )}
    </>
  );
}
